import type { PlayerInterface } from '../../interfaces/player/player.interface';

/**
 * Premium table cosmetics ("Tischtextur", "Getränk am Tisch").
 * Catalogs mirror backend/api/services/premium.service.ts (first entry = default).
 * The backend omits `tableTexture` / `drink` on table members for defaults and
 * non-premium users, so unknown/missing values always fall back to the default.
 */
export const TABLE_TEXTURES = [
  'klassisch',
  'eiche_dunkel',
  'gruener_filz',
  'weinrot',
] as const;
export type TableTextureType = (typeof TABLE_TEXTURES)[number];

export const DRINKS = ['none', 'bier', 'spritzer', 'limonade'] as const;
export type DrinkType = (typeof DRINKS)[number];

export namespace TableTextureType {
  export const DEFAULT: TableTextureType = TABLE_TEXTURES[0];

  export function normalize(value: unknown): TableTextureType {
    return (TABLE_TEXTURES as readonly unknown[]).includes(value)
      ? (value as TableTextureType)
      : DEFAULT;
  }

  /**
   * The table texture is a personal view: it is drawn with MY OWN choice.
   * `players` is the display-rotated list (idx 0 = me); spectators (myIdx -1)
   * always see the default table.
   */
  export function forViewer(
    players: PlayerInterface[] | undefined,
    myIdx: number
  ): TableTextureType {
    if (myIdx === -1 || myIdx === undefined || myIdx === null) return DEFAULT;
    return normalize(players?.[0]?.tableTexture);
  }

  /** CSS modifier class for the table surface; '' for the default (unchanged look) */
  export function getCssClass(texture: TableTextureType): string {
    return texture === DEFAULT ? '' : `table-texture-${texture}`;
  }

  /**
   * Pre-rendered 3D table image. The variants share table.png's exact silhouette
   * and lighting (see /assets/images/table-textures/CREDITS.md), so only the src changes.
   */
  export function get3dTableSrc(texture: TableTextureType): string {
    return texture === DEFAULT
      ? '/assets/images/3d/table.png'
      : `/assets/images/table-textures/3d/table-${texture}.webp`;
  }
}

export namespace DrinkType {
  export const NONE: DrinkType = DRINKS[0];

  export function normalize(value: unknown): DrinkType {
    return (DRINKS as readonly unknown[]).includes(value)
      ? (value as DrinkType)
      : NONE;
  }

  export function ofPlayer(player: PlayerInterface | undefined): DrinkType {
    return normalize(player?.drink);
  }

  /**
   * Blender renders of each drink per camera: `top` = (near) top-down for the
   * 2D/mobile tables, `near`/`mid`/`far` = the 3D tavern perspective for the
   * front, side and back seats. All drinks of one view share scale and framing,
   * so they keep their real size relative to each other.
   */
  export type View = 'top' | 'near' | 'mid' | 'far';

  /** image shown on the table, undefined when no drink is set */
  export function getSrc(
    drink: DrinkType,
    view: View = 'top'
  ): string | undefined {
    if (normalize(drink) === NONE) return undefined;
    return `/assets/images/drinks/${drink}-${view}.webp`;
  }

  export function getLabel(drink: DrinkType): string {
    if (drink === 'bier') return 'Krügerl Bier';
    if (drink === 'spritzer') return 'Spritzer';
    if (drink === 'limonade') return 'Limonade';
    return '';
  }
}
